import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'

import Title from 'components/atoms/Title'
import InputControl from 'components/molecules/InputControl'
import InputDate from 'components/molecules/InputDate'
import Checkbox from 'components/molecules/Checkbox'

import { OrderFields } from 'types/order'

import { selectOrderFormErrors, selectOrderWrite } from 'store/order/order.selectors'
import { changeOrderHandler } from 'store/order/order.actions'

import { getDateForServer } from 'utils/date'
import { useInsuranceForm } from '../../hooks/useInsuranceForm'
import { InsuranceProps } from './index'

const Customer: React.FC<InsuranceProps> = ({ onChange }) => {
  const dispatchStore = useDispatch()
  const form = useSelector(selectOrderWrite)
  const errors = useSelector(selectOrderFormErrors)

  const { insuranceCost } = useInsuranceForm({ form, onChange })

  const setInsuranceConfirmed = useCallback(
    (value: boolean) => {
      onChange(value, 'meta.insuranceConfirmed')
      onChange(form[OrderFields.ROUTE_DATE_START], OrderFields.ROUTE_DATE_START)
      onChange(form[OrderFields.ROUTE_TRIP], OrderFields.ROUTE_TRIP)
      onChange(form[OrderFields.CARGO], OrderFields.CARGO)
    },
    [form, onChange],
  )

  const setRouteDate = (daysToAdd: number) => {
    const date = new Date()
    date.setDate(date.getDate() + daysToAdd)
    dispatchStore(changeOrderHandler({ [OrderFields.ROUTE_DATE_START]: getDateForServer(date) }))
  }

  return (
    <div
      style={{
        backgroundColor: '#e9fbe5',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid #cdeccd',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '24px',
      }}
    >
      <Title text="Страхование груза" />
      <div style={{ margin: '4px -16px' }}>
        <div role="alert" className="fade m-0 alert alert-info show align-items-center">
          Давайте застрахуем ваш груз!
        </div>
      </div>
      <ul className="list-group needs-validation">
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="text"
            id="meta.insuredInn"
            value={(form[OrderFields.META]?.insuredInn as string) ?? ''}
            isValid={errors[OrderFields.META]?.insuredInn?.isValid}
            invalidMessage={errors[OrderFields.META]?.insuredInn?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="ИНН страхующего:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="text"
            id="meta.insuredName"
            value={(form[OrderFields.META]?.insuredName as string) ?? ''}
            isValid={errors[OrderFields.META]?.insuredName?.isValid}
            invalidMessage={errors[OrderFields.META]?.insuredName?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Название страхующего:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="email"
            id="meta.insuredEmail"
            value={(form[OrderFields.META]?.insuredEmail as string) ?? ''}
            isValid={errors[OrderFields.META]?.insuredEmail?.isValid}
            invalidMessage={errors[OrderFields.META]?.insuredEmail?.invalidMessage || ''}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Email, куда отправить полис:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputDate
            id={OrderFields.ROUTE_DATE_START}
            value={form[OrderFields.ROUTE_DATE_START]}
            isValid={errors[OrderFields.ROUTE_DATE_START]?.isValid}
            invalidMessage={errors[OrderFields.ROUTE_DATE_START]?.invalidMessage || ''}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Дата начала рейса:"
          />
          <div className="col-lg-4 col-md-6 col-sm-12 p-0 d-flex" style={{ marginTop: '12px' }}>
            <Button className="p-0" style={{ flex: 1, marginRight: '4px' }} onClick={() => setRouteDate(0)}>
              Сегодня
            </Button>
            <Button className="p-0" style={{ flex: 1, marginLeft: '4px' }} onClick={() => setRouteDate(1)}>
              Завтра
            </Button>
          </div>
        </li>
        <li className="list-group-item row bg-transparent">
          <InputDate
            id="meta.routeDateEnd"
            value={(form[OrderFields.META]?.routeDateEnd as string) || ''}
            isValid={errors[OrderFields.META]?.routeDateEnd?.isValid}
            invalidMessage={errors[OrderFields.META]?.routeDateEnd?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Дата конца рейса:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="text"
            id={OrderFields.ROUTE_TRIP}
            value={form[OrderFields.ROUTE_TRIP]}
            isValid={errors[OrderFields.ROUTE_TRIP]?.isValid}
            invalidMessage={errors[OrderFields.ROUTE_TRIP]?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Маршрут:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="text"
            id={OrderFields.CARGO}
            value={form[OrderFields.CARGO] || ''}
            isValid={errors[OrderFields.CARGO]?.isValid}
            invalidMessage={errors[OrderFields.CARGO]?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Груз:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          <InputControl
            type="number"
            id="meta.costOfCargo"
            value={(form[OrderFields.META]?.costOfCargo as string) || ''}
            isValid={errors[OrderFields.META]?.costOfCargo?.isValid}
            invalidMessage={errors[OrderFields.META]?.costOfCargo?.invalidMessage}
            onChangeHandler={onChange}
            className="col-lg-4 col-md-6 col-sm-12"
            label="Стоимость груза:"
          />
        </li>
        <li className="list-group-item row bg-transparent">
          {insuranceCost !== null && <strong>Расчет страховой премии: {insuranceCost.toLocaleString()} руб.</strong>}
          <Checkbox
            id="meta.insuranceConfirmed"
            value={(form[OrderFields.META]?.insuranceConfirmed as boolean) || false}
            onChangeHandler={(value) => setInsuranceConfirmed(value)}
            label="Да, давайте застрахуем. Хочу получить готовый полис на почту."
          />
        </li>
      </ul>
    </div>
  )
}

export default Customer
